/**
 * Photo download/open helpers.
 *
 * The backend's photo URLs (`Storage::disk('public')->url()`) are rooted at
 * Laravel's own `APP_URL`, which is a DIFFERENT host than this SPA in both
 * environments: production serves the SPA from yallabusiness-sy.com and the
 * backend from ceremony.yallabusiness-sy.com, and local dev runs the SPA on
 * :3000 against a backend on :8000. So photos are normally cross-origin — the
 * "open in a new tab" path below is the common case, not the edge case.
 * Because that still depends on backend env vars this repo cannot see
 * (`APP_URL`, `GUESTS_PHOTO_DISK`), the origin is checked at runtime rather
 * than assumed either way.
 *
 * This matters more than it might look: a `download`-attributed `<a>` clicked
 * cross-origin doesn't fail quietly — the browser ignores `download` and
 * follows the href, navigating the guest's own invitation tab to a raw image
 * and losing their `access_key` from the address bar. So cross-origin photos
 * are never downloaded programmatically; they're opened in a new tab instead
 * (`window.open(..., '_blank', 'noopener,noreferrer')`), and the UI labels
 * that control accurately rather than promising a save it can't perform.
 */

export function isSameOrigin(url) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false; // malformed/unparsable URL — treat as unsafe to auto-download
  }
}

/**
 * Save (same-origin) or open-in-a-new-tab (cross-origin) a single photo.
 * Never throws — a malformed URL simply does nothing.
 */
export function downloadPhoto(photo) {
  if (!photo?.url) return;

  if (isSameOrigin(photo.url)) {
    const a = document.createElement('a');
    a.href = photo.url;
    if (photo.original_name) a.download = photo.original_name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(photo.url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Download every same-origin photo in the list, staggered so the browser
 * doesn't treat a burst of clicks as a popup flood. Cross-origin photos are
 * skipped here on purpose — see the module comment. Callers should only
 * offer this control at all when every photo is same-origin (see
 * PhotoGallery.js), but it's defensive here too.
 */
export function downloadAll(photos) {
  const downloadable = (photos || []).filter((p) => p?.url && isSameOrigin(p.url));
  downloadable.forEach((photo, index) => {
    setTimeout(() => downloadPhoto(photo), index * 300);
  });
}

/**
 * Human-readable file size, Arabic units. Guards against a missing/zero/
 * non-numeric `size` (never renders "NaN" or "undefined").
 */
export function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) return '';

  const units = ['بايت', 'ك.ب', 'م.ب', 'غ.ب'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
}
